import { createContext, useCallback, useContext, useRef } from "react";

/**
 * Master v5 Prompt 9 — Room-scoped registry of every currently-mounted
 * block's `stopEditing` (flush pending autosave + release the lease).
 *
 * "Save & Exit should finish pending saves where possible and release
 * held leases" (locked rule) requires the Room to actively resolve every
 * open lease BEFORE navigating away — unmount alone is not enough,
 * because useBlockLease's own unmount cleanup only releases the lease,
 * it deliberately never flushes a still-debounced save (see
 * useBlockLease.js). This registry is what lets RoomShell call every
 * mounted block's `stopEditing` first, then navigate.
 */
const RoomBlockRegistryContext = createContext(null);

export function RoomBlockRegistryProvider({ children }) {
  const registryRef = useRef(new Map());
  const dirtyRef = useRef(new Set());
  const dirtySubscribersRef = useRef(new Set());

  const register = useCallback((blockId, stopEditing) => {
    registryRef.current.set(blockId, stopEditing);
  }, []);

  const unregister = useCallback((blockId) => {
    registryRef.current.delete(blockId);
  }, []);

  const flushAll = useCallback(async () => {
    const fns = [...registryRef.current.values()];
    await Promise.all(fns.map((fn) => fn().catch(() => {})));
  }, []);

  // "Navigation blocking must apply only for genuinely unsaved local
  // state" — this is that live signal. A block reports dirty=true the
  // instant its own autosave debounce timer is pending, and dirty=false
  // the instant a save resolves (see useBlockLease's `dirty` state).
  const setBlockDirty = useCallback((blockId, isDirty) => {
    if (isDirty) dirtyRef.current.add(blockId);
    else dirtyRef.current.delete(blockId);
    const hasUnsaved = dirtyRef.current.size > 0;
    dirtySubscribersRef.current.forEach((cb) => cb(hasUnsaved));
  }, []);

  const subscribeDirty = useCallback((cb) => {
    dirtySubscribersRef.current.add(cb);
    cb(dirtyRef.current.size > 0);
    return () => dirtySubscribersRef.current.delete(cb);
  }, []);

  return (
    <RoomBlockRegistryContext.Provider value={{ register, unregister, flushAll, setBlockDirty, subscribeDirty }}>
      {children}
    </RoomBlockRegistryContext.Provider>
  );
}

export function useRoomBlockRegistry() {
  const ctx = useContext(RoomBlockRegistryContext);
  if (!ctx) throw new Error("useRoomBlockRegistry must be used within RoomBlockRegistryProvider");
  return ctx;
}
